/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import { ThumbUp as ThumbUpIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { SearchBar } from 'components/SearchBar';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { Query, useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { getAllCompaniesRequest } from 'modules/companies/actions';
import {
  CompanyFields,
  ListCompanyDataProps,
} from 'modules/companies/interfaces';
import { modalClose } from 'modules/modal/actions';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import { CompanyFieldsProps } from '.';
import styles from './styles';

const useStyles = makeStyles(styles);

interface CompanyListProps {
  closeOnSelect?: boolean;
  companyFields: CompanyFieldsProps | null;
  companyList: ListCompanyDataProps;
  initialQuery?: Query;
  isLoading: boolean;
  stateKey: string;
  getAllCompaniesRequest: (props) => void;
  modalClose: () => void;
  setCompanyFields: (prop: CompanyFieldsProps | null) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
}

const CompanyList = ({
  closeOnSelect,
  companyFields,
  companyList,
  initialQuery = { offset: 0, limit: 10, search: '' },
  isLoading,
  stateKey,
  getAllCompaniesRequest,
  modalClose,
  setCompanyFields,
  setFieldValue,
}: CompanyListProps) => {
  const classes = useStyles();
  const {
    itemOrder,
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const headers: TableCellHeaderProps[] = [
    {
      key: 'display_name',
      i18n: 'COMPANY_NAME_KEY',
      order: true,
      style: { width: '40%' },
    },
    {
      key: 'trading_name',
      i18n: 'SINGLE_CORPORATE_NAME_KEY',
      order: true,
      style: { width: '40%' },
    },
    {
      key: 'zoho_id',
      i18n: 'ZOHO_ID_KEY',
      style: { width: '20%' },
    },
  ];

  const getCompanies = () => {
    getAllCompaniesRequest({
      ...query,
    });
  };

  React.useEffect(() => {
    getCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <Grid container>
      <SearchBar
        id="search-companies"
        isLoading={isLoading}
        handleSearch={handleSearch}
        setSearchValue={setSearchValue}
      />
      <TableItems
        header={
          <>
            <TableCell>
              <></>
            </TableCell>
            {headers.map(item => (
              <TableCell
                key={item?.i18n}
                order={item?.order}
                style={item?.style}
                markOderItem={itemOrder === item?.key}
                handleOrder={() =>
                  item?.order ? handleOrder(item?.key) : null
                }
              >
                {i18n.t(item?.i18n)}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: CompanyFields) => (
          <TableRow
            key={item?.company_uuid}
            onClick={() => {
              setFieldValue(stateKey, item?.company_uuid);
              setCompanyFields({
                company_name: item?.company_name,
                company_uuid: item?.company_uuid,
              });
              if (closeOnSelect) {
                modalClose();
              }
            }}
            className={clsx(classes.tableRow, {
              [classes.companySelected]:
                companyFields?.company_uuid === item?.company_uuid,
            })}
          >
            <TableCell className={classes.likeCell}>
              {companyFields?.company_uuid === item?.company_uuid ? (
                <ThumbUpIcon fontSize="small" />
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell ellipsis>{item?.company_name}</TableCell>
            <TableCell ellipsis>{item?.trading_name}</TableCell>
            <TableCell>{item?.zoho_id}</TableCell>
          </TableRow>
        )}
        data={companyList}
        isLoading={isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        overrideRowsCount={query.limit}
        notFoundText={i18n.t('NO_COMPANY_KEY')}
      />
    </Grid>
  );
};

const mapStateToProps = ({
  companies: { companyList, isLoading },
}: ApplicationState) => ({ companyList, isLoading });

export default connect(mapStateToProps, { getAllCompaniesRequest, modalClose })(
  CompanyList
);
